/**
 * Mermaid 图表错误检查与修复面板
 */
import React from 'react';
import { OutlineItem } from '../types';
import {
  CheckCircleIcon,
  XMarkIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

interface MermaidError {
  line: number;
  message: string;
  snippet: string;
  context?: string;
  expected?: string;
  got?: string;
}

interface ChapterMermaidErrors {
  item: OutlineItem;
  errors: MermaidError[];
}

interface ContentEditMermaidPanelProps {
  show: boolean;
  errors: ChapterMermaidErrors[] | null;
  fixingIds: Set<string>;
  fixedIds: Set<string>;
  expandedErrors: Set<string>;
  fixingAll: boolean;
  fixProgress: { current: number; total: number };
  onClose: () => void;
  onEditChapter: (item: OutlineItem) => void;
  onFixChapter: (chapter: ChapterMermaidErrors) => void;
  onFixAll: () => void;
  onToggleExpandError: (key: string) => void;
  readOnly?: boolean;
}

const ContentEditMermaidPanel: React.FC<ContentEditMermaidPanelProps> = ({
  show,
  errors,
  fixingIds,
  fixedIds,
  expandedErrors,
  fixingAll,
  fixProgress,
  onClose,
  onEditChapter,
  onFixChapter,
  onFixAll,
  onToggleExpandError,
  readOnly = false,
}) => {
  if (!show || errors === null) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col z-10">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Mermaid 图表检查结果
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-6 space-y-4">
          {errors.length === 0 ? (
            <div className="flex items-center text-green-600 py-4">
              <CheckCircleIcon className="w-5 h-5 mr-2" />
              <span>所有 mermaid 图表均无语法错误</span>
            </div>
          ) : (
            errors.map((chapter, idx) => (
              <div key={chapter.item.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-xs text-gray-400 mr-2">#{idx + 1}</span>
                    <span className="font-medium text-gray-900 text-sm">
                      {chapter.item.id} {chapter.item.title}
                    </span>
                    <span className="ml-2 text-xs text-red-500">
                      ({chapter.errors.length} 个错误)
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!readOnly && (
                    <button
                      onClick={() => {
                        onClose();
                        onEditChapter(chapter.item);
                      }}
                      className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                    >
                      <PencilSquareIcon className="w-3 h-3 mr-1" />
                      编辑
                    </button>
                    )}
                    {fixedIds.has(chapter.item.id) ? (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-green-700 bg-green-50 rounded">
                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                        已修复
                      </span>
                    ) : (
                      <button
                        onClick={() => onFixChapter(chapter)}
                        disabled={fixingIds.has(chapter.item.id)}
                        className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
                      >
                        {fixingIds.has(chapter.item.id) ? (
                          <>
                            <svg className="animate-spin w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            修复中...
                          </>
                        ) : (
                          <>
                            <ArrowPathIcon className="w-3 h-3 mr-1" />
                            一键修复
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  {chapter.errors.map((err, errIdx) => {
                    const expandKey = `${chapter.item.id}-${errIdx}`;
                    const isExpanded = expandedErrors.has(expandKey);
                    const isLongMessage = err.message.length > 80;
                    return (
                      <div key={errIdx} className="text-xs bg-gray-50 rounded">
                        <button
                          onClick={() => onToggleExpandError(expandKey)}
                          className="w-full text-left px-3 py-1.5 flex items-start hover:bg-gray-100 rounded transition-colors group"
                        >
                          <span className="text-red-400 font-mono mr-2 flex-shrink-0 mt-px">行{err.line}</span>
                          <span className={`text-red-600 flex-1 min-w-0 ${isExpanded ? '' : 'line-clamp-1'}`}>
                            {err.message}
                          </span>
                          <code className={`text-gray-500 flex-shrink-0 max-w-[120px] truncate ml-2 ${isExpanded ? 'hidden' : ''}`}>
                            {err.snippet}
                          </code>
                          {isLongMessage && (
                            <ChevronDownIcon
                              className={`w-3.5 h-3.5 ml-1.5 flex-shrink-0 text-gray-400 group-hover:text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            />
                          )}
                        </button>
                        {isExpanded && (
                          <div className="px-3 pb-2 space-y-1.5">
                            {(err.expected || err.got) && (
                              <div className="flex items-center gap-2 text-gray-500">
                                {err.expected && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 bg-green-50 text-green-700 rounded font-mono">
                                    期望: {err.expected}
                                  </span>
                                )}
                                {err.got && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 bg-red-50 text-red-700 rounded font-mono">
                                    实际: {err.got}
                                  </span>
                                )}
                              </div>
                            )}
                            {err.context && (
                              <div className="text-gray-500">
                                <span className="text-gray-400">错误位置：</span>
                                <code className="block mt-0.5 text-gray-600 whitespace-pre-wrap font-mono bg-gray-100 rounded px-2 py-1">
                                  {err.context}
                                </code>
                              </div>
                            )}
                            <div className="text-gray-500">
                              <span className="text-gray-400">出错行代码：</span>
                              <code className="block mt-0.5 text-gray-600 whitespace-pre-wrap break-all font-mono bg-gray-100 rounded px-2 py-1">
                                {err.snippet}
                              </code>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
        <div className="px-6 py-3 border-t border-gray-200 flex justify-between">
          <div>
            {errors.length > 0 && (
              <button
                onClick={onFixAll}
                disabled={fixingAll}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
              >
                {fixingAll ? (
                  <>
                    <svg className="animate-spin w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    正在修复 {fixProgress.current}/{fixProgress.total}...
                  </>
                ) : (
                  <>
                    <ArrowPathIcon className="w-4 h-4 mr-1.5" />
                    全部修复
                  </>
                )}
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentEditMermaidPanel;
